import { forwardRef, useImperativeHandle } from 'react'
import { GridToolbarContainer, GridToolbarExport, DataGrid as MuiDataGrid, useGridApiContext } from '@mui/x-data-grid'
import { Tooltip } from '@mui/material'
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons'

import { Box, Divider, Icons, LinearProgress, Text, ToggleButtons } from '..'
import { TroubleMessage } from '../../pages/charities/trouble-message'

export function DataGridToolbar({
  filters,
  title,
  disableExport,
  csvOptions,
  loading,
  viewToggleOnClicks,
  viewToggleState,
  showTroubleMessage,
  toolbarActions,
  ...props
}) {
  /* when no title is provided, show filters in place of the title next to the export button,
   * instead of putting filters on its own row */
  return (
    <>
      <GridToolbarContainer {...props} sx={{ flexWrap: 'wrap', justifyContent: 'space-between', mb: 2 }}>
        {/* flexGrow/minWidth: 0 -- without them a block-level title (e.g.
            PortfolioSummary's own internal grid, investment-portfolio-
            activity.jsx) shrinks to its own content width here instead of
            filling the row up to the export button/toolbarActions group,
            same class of bug as a flex child needing to cede/claim space
            explicitly rather than relying on an ambiguous auto size. Plain
            string/short titles are unaffected -- they just gain unused
            width to their right, which a left-aligned heading doesn't show. */}
        {title ? <Text.H2 sx={{ flexGrow: 1, minWidth: 0 }}>{title}</Text.H2> : filters}
        {viewToggleOnClicks && viewToggleState && (
          <ToggleButtons onClicks={viewToggleOnClicks} viewToggleState={viewToggleState} />
        )}
        {showTroubleMessage && <TroubleMessage />}
        {/* toolbarActions and the export button are direct row-siblings in
            the INNER box below, both plain buttons with no height difference
            between them, so alignItems: center actually lines up the two
            buttons themselves -- previously the export button shared its row
            with the (taller) caption/tooltip line stacked below it, so
            centering against that taller box misaligned the two buttons.
            The caption now sits on its own row underneath instead. The
            whole group carries marginLeft: auto to stay pushed to the far
            right of the toolbar. */}
        {(toolbarActions || !disableExport) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {toolbarActions}
              {!disableExport && (
                <GridToolbarExport
                  csvOptions={csvOptions}
                  printOptions={{ disableToolbarButton: true }}
                  sx={{
                    fontSize: '16px',
                    px: 3,
                    py: 1,
                  }}
                />
              )}
            </Box>
            {!disableExport && (
              <Tooltip
                title="Detailed Report includes: gift designations, processing fees, courtesy fees, and more"
                arrow
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Icons.FaIcon icon={faCircleQuestion} />
                  <Text.Body sx={{ ml: 0.5, fontSize: '14px' }}>Download a detailed activity report</Text.Body>
                </div>
              </Tooltip>
            )}
          </Box>
        )}
        {title && filters && <Box width="100%">{filters}</Box>}
      </GridToolbarContainer>
      {loading && <LinearProgress />}
      <Divider light />
    </>
  )
}

export const DataGrid = forwardRef(
  (
    {
      apiRef,
      filters,
      sx,
      components,
      componentsProps,
      title,
      disableExport,
      loading,
      pageSize,
      rowHeight,
      autoRowHeight = !rowHeight,
      exportFileName,
      viewToggleOnClicks,
      viewToggleState,
      showTroubleMessage,
      toolbarActions,
      hideColumnHeaders,
      ...props
    },
    ref
  ) => (
    <MuiDataGrid
      ref={ref}
      pageSize={pageSize ?? 20}
      autoHeight
      disableSelectionOnClick
      ignoreSelectionOnExport
      {...(autoRowHeight && !rowHeight && { getRowHeight: () => 'auto' })}
      {...(rowHeight && { rowHeight })}
      sx={{
        width: '100%',
        border: 'none',
        ...(hideColumnHeaders && {
          '.MuiDataGrid-columnHeaders': {
            display: 'none',
          },
          '.MuiDataGrid-virtualScroller': {
            marginTop: '0px !important',
          },
        }),
        '.MuiDataGrid-columnHeadersWrapper': {
          height: 0,
        },
        // '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer': {
        //   fontWeight: 600,
        //   fontSize: '1rem',
        // },
        '& .MuiDataGrid-toolbarContainer': {
          padding: 0,
        },
        '& .MuiDataGrid-overlay': {
          opacity: 0.9,
          backgroundColor: 'white',
          width: '100%',
          position: 'relative',
          zIndex: 10,
        },
        ...(autoRowHeight && {
          '& .MuiDataGrid-cell': {
            py: '6px',
          },
        }),
        ...sx,
      }}
      components={{
        Toolbar: (p) => {
          /* This is a werid hack to make the apiRef property work on DataGrid.
           *
           * Docs say: https://mui.com/x/react-data-grid/api-object/#how-to-use-the-api-object
           *
           * > The API object is accessible through the apiRef variable.
           * > Depending on where you are trying to access this variable,
           * > you will have to use either useGridApiContext or useGridApiRef
           *
           * It looks like there's an useGridApiRef() and apiRef property
           * similar to react's useRef() and ref property, but it doesn't seem
           * to work and it's not very well documented so I have no idea. But I
           * just learned about useImperativeHandle() and since useGridApiContext()
           * _appears_ to work, we can just call that inside this "component"
           * and forward it to apiRef property using react's useRef() ... */

          if (exportFileName) {
            const { csvOptions } = p
            csvOptions.fileName = exportFileName
          }

          const gridApi = useGridApiContext()
          useImperativeHandle(apiRef, () => gridApi.current)
          return DataGridToolbar(p)
        },
        ...components,
      }}
      componentsProps={{
        toolbar: {
          csvOptions: { allColumns: true },
          title,
          disableExport,
          loading,
          filters,
          viewToggleOnClicks,
          viewToggleState,
          showTroubleMessage,
          toolbarActions,
          rowHeight,
          hideColumnHeaders,
        },
        ...componentsProps,
      }}
      loading={loading}
      {...props}
    />
  )
)
